import type { BadgeTone } from '@hrms/ui';
import type {
  GroupLearningItemStep,
  GroupLearningRun,
  GroupLearningRunItem,
  GroupLearningSkipReason,
} from '@hrms/shared';
import { SKIP_REASON_LABEL } from '../../routes/admin/training/course/assign-course.js';

/**
 * Pure rules behind the progress panel of a copy / assign run (PLAN-group-lms.md §2.2): when to keep
 * polling, what each company's step says, and when Retry is offered.
 */

/** How often the panel asks again while something is still running. */
export const RUN_POLL_MS = 2000;
/** Stop asking this long after the run was created, whatever it says: a run stuck as "waiting"
 *  (the worker is down) must not poll for ever from an open tab. */
export const RUN_POLL_GIVE_UP_MS = 30 * 60_000;

/** Steps that will change by themselves. `interrupted` is not one: it waits for Retry. */
const MOVING_STEPS: readonly GroupLearningItemStep[] = ['queued', 'copying', 'assigning'];

export function isMovingStep(step: GroupLearningItemStep): boolean {
  return MOVING_STEPS.includes(step);
}

export function isRunActive(run: GroupLearningRun | null | undefined): boolean {
  if (!run) return false;
  return run.items.some((item) => isMovingStep(item.step));
}

/** The run in a typed response, or null for anything that is not a 200 / 202 with a run. */
export function runFromResponse(
  response: { status: number; body: unknown } | undefined,
): GroupLearningRun | null {
  if (!response || (response.status !== 200 && response.status !== 202)) return null;
  return response.body as GroupLearningRun;
}

/** The `refetchInterval` for the run query: a number while it is moving, `false` once it is not. */
export function nextPollInterval(run: GroupLearningRun | null | undefined, now: number = Date.now()): number | false {
  if (!run) return false;
  if (now - Date.parse(run.createdAt) > RUN_POLL_GIVE_UP_MS) return false;
  return isRunActive(run) ? RUN_POLL_MS : false;
}

export const ITEM_STEP_LABEL: Record<GroupLearningItemStep, string> = {
  queued: 'Waiting',
  copying: 'Copying the course',
  assigning: 'Assigning',
  done: 'Done',
  failed: 'Failed',
  skipped: 'Skipped',
  interrupted: 'Stopped part way',
};

export const ITEM_STEP_TONE: Record<GroupLearningItemStep, BadgeTone> = {
  queued: 'neutral',
  copying: 'info',
  assigning: 'info',
  done: 'success',
  failed: 'danger',
  skipped: 'warning',
  interrupted: 'warning',
};

export const COMPANY_SKIP_TEXT: Record<GroupLearningSkipReason, string> = {
  no_access: "You can't manage training in this company any more.",
  training_off: "Training isn't available in this company.",
  source_unavailable: "The course couldn't be read in the company it comes from.",
};

/** Retry is for a company that failed, was cut off, or was skipped for a reason that can change. */
export function canRetry(item: GroupLearningRunItem): boolean {
  return item.step === 'failed' || item.step === 'interrupted' || item.step === 'skipped';
}

/** "3 already have it, 1 are not active employees" for the per-person skips of one company. */
export function personSkipText(item: GroupLearningRunItem): string {
  return item.skippedReasons
    .filter((entry) => entry.count > 0)
    .map((entry) => `${entry.count} ${SKIP_REASON_LABEL[entry.reason]}`)
    .join(', ');
}

/** One short line for a company's row. */
export function itemSummary(kind: GroupLearningRun['kind'], item: GroupLearningRunItem): string {
  switch (item.step) {
    case 'done':
      if (kind === 'copy') return 'Copied.';
      return item.assignedCount === 1 ? 'Assigned to 1 person.' : `Assigned to ${item.assignedCount} people.`;
    case 'skipped':
      return item.skipReason ? COMPANY_SKIP_TEXT[item.skipReason] : 'Skipped.';
    case 'failed':
      return item.error ?? 'Something went wrong.';
    case 'interrupted':
      return 'This stopped before it finished. Try again to carry on; nothing is done twice.';
    case 'copying':
      return 'Copying the course into this company.';
    case 'assigning':
      return 'Assigning the course to people.';
    default:
      return 'Waiting for its turn.';
  }
}

/** The line above the list: what the whole run is doing. */
export function runHeadline(run: GroupLearningRun): string {
  const total = run.items.length;
  const done = run.items.filter((item) => item.step === 'done').length;
  const verb = run.kind === 'copy' ? 'Copying' : 'Assigning';
  const name = `"${run.source.courseTitle}"`;
  switch (run.status) {
    case 'queued':
    case 'running':
      return `${verb} ${name}: ${done} of ${total} ${total === 1 ? 'company' : 'companies'} done`;
    case 'done':
      return run.kind === 'copy' ? `${name} was copied to ${total} ${total === 1 ? 'company' : 'companies'}` : `${name} was assigned in ${total} ${total === 1 ? 'company' : 'companies'}`;
    case 'partial':
      return `${name}: ${done} of ${total} ${total === 1 ? 'company' : 'companies'} done, some need attention`;
    default:
      return `${name} could not be ${run.kind === 'copy' ? 'copied' : 'assigned'}`;
  }
}

/** Total people assigned across a finished run. */
export function totalAssigned(run: GroupLearningRun): number {
  return run.items.reduce((sum, item) => sum + item.assignedCount, 0);
}
